import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Check, Home } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import BottomActionBar from '../../components/BottomActionBar'
import { casesAPI } from '../../services/api'
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { formatUsPhone, FAX_STEPS, faxStepIndex, isFaxFinal } from '../../utils/caseReport'
import { officeConfirmed, officeStreet, officeCityLine } from '../../utils/officeAddress' // s110
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const BODY = '#5A7185'
const MUTE = '#93A5B4'

/**
 * SendReportScreen — s98 Phases 6 + 7 (D468). Approved design: mockup C v3, "Send your Case
 * Report" and "Sent".
 *   confirm  the office, phone and fax (from the directory, or the doctor's own Orella profile,
 *            R39); "Wrong number? Enter it"; a missing fax is typed here; what we send; the
 *            consent box (his wording); Send. POST /cases/<id>/fax-sends.
 *   sent     Queued -> Sending -> Delivered, or Failed + Try again, read back from the server
 *            (GET /cases/<id>/fax-sends; the server re-asks Sinch for a send still moving).
 * ★ No success is shown that the server did not report (check-fabricated-success).
 * ★ Reached three ways: from the finder ({provider}), from the hub's failed row ({retry}) and
 *   from the hub's moving row ({sent}).
 */
export default function SendReportScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const currentCase = useCaseStore((s) => s.currentCase)
  const { t, hasText } = useI18n()
  const st = location.state || {}
  const provider = st.provider || (st.retry ? {
    npi: st.retry.npi, name: st.retry.recipientName, practice: st.retry.recipientPractice, fax: st.retry.faxNumber,
  } : null)
  const [phase, setPhase] = useState(st.sent ? 'sent' : 'confirm')
  const [send, setSend] = useState(st.sent || null)
  const [editing, setEditing] = useState(!provider?.fax)
  const [fax, setFax] = useState('')
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pollRef = useRef(null)

  useEffect(() => { window.scrollTo(0, 0) }, [phase])

  // The Sent screen reads the server until the send is final: 4 s, then 8 s, capped at 20 s.
  useEffect(() => {
    if (phase !== 'sent' || !send?.id || isFaxFinal(send.status) || !currentCase?.id) return
    let delay = 4000
    let alive = true
    const tick = async () => {
      try {
        const r = await casesAPI.faxSends(currentCase.id)
        const mine = (r?.sends || []).find((x) => x.id === send.id)
        if (alive && mine) setSend(mine)
        if (mine && isFaxFinal(mine.status)) return
      } catch (e) { /* the next tick tries again */ }
      if (!alive) return
      delay = Math.min(delay * 2, 20000)
      pollRef.current = setTimeout(tick, delay)
    }
    pollRef.current = setTimeout(tick, delay)
    return () => { alive = false; clearTimeout(pollRef.current) }
  }, [phase, send?.id, send?.status, currentCase?.id])

  if (!currentCase) return <Navigate to="/home" replace />
  if (!provider && !send) return <Navigate to="/case/share-hub" replace />

  const typedOk = fax.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length === 10
  const number = editing ? fax : (provider?.fax || '')
  const canSend = consent && !busy && (editing ? typedOk : !!provider?.fax)
  const name = provider?.name || send?.recipientName || ''
  // s110: the office line is the practice (when named) over its street and city; an address not confirmed
  //   shows city and ZIP only, a badge and a note (mockup screen 4).
  const confirmed = officeConfirmed(provider)
  const officeAddr = [officeStreet(provider), officeCityLine(provider)].filter(Boolean).join(', ')
  const office = [provider?.practice, officeAddr].filter(Boolean).join(' · ')

  async function doSend() {
    if (!canSend) return
    setBusy(true)
    setError('')
    try {
      const r = await casesAPI.faxSend(currentCase.id, {
        faxNumber: number, consent: true, npi: provider?.npi || undefined,
        // s123 (board F, his ruling): "Any clinician at this office" — the cover reads "To: Any available clinician" over
        //   the practice (server-written fax copy is English, D483); the app shows the practice's name.
        recipientName: (provider?.anyClinician ? 'Any available clinician' : name) || undefined, recipientPractice: provider?.practice || undefined,
        recipientPhone: provider?.phone || undefined,
      })
      if (r?.send?.id) { setSend(r.send); setPhase('sent') }
      else setError(t('sendReport.failed', "We couldn't send your Case Report. Please try again."))
    } catch (e) {
      if (e?.send?.id) { setSend(e.send); setPhase('sent') }
      else setError(e?.code === 'fax_invalid' ? t('sendReport.badNumber', 'Enter a valid US fax number.')
        : e?.code === 'fax_unavailable' ? t('sendReport.unavailable', 'Sending by fax is not available right now.')
          : e?.code === 'fax_daily_limit' ? t('sendReport.limit', 'This report was faxed several times today. Please try again tomorrow.')
            : t('sendReport.failed', "We couldn't send your Case Report. Please try again."))
    } finally {
      setBusy(false)
    }
  }

  if (phase === 'sent') {
    const failed = send?.status === 'failed'
    const idx = faxStepIndex(send?.status)
    const to = formatUsPhone(send?.faxNumber) || send?.faxNumber || ''
    return (
      <div className="tabbar-screen-min-h bg-gray-50 flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepLabel={t('shareHub.stepLabel', 'Share')} statusLabel={t('sendReport.sentPill', 'Sent')}
                       onBack={() => navigate('/case/share-hub')} onNotification={() => navigate('/notifications')} />
        <OrellaChatBubble instant animationKey={`fax-${send?.id}-${send?.status}`}
          message={failed ? t('sendReport.failedMsg', "Your Case Report didn't go through. You can try again.")
            : send?.status === 'delivered'
              ? t('sendReport.deliveredMsg', { defaultValue: "Your Case Report was delivered to {{name}}'s office. Call them to book, and mention your share code.", name: send?.recipientName || send?.recipientPractice || '' })
              : t('sendReport.sendingMsg', "Sending your Case Report now. I'll let you know when it's delivered.")} />
        <div className="flex-1 px-4 pt-1 pb-6 flex flex-col gap-3">
          <div className="bg-white rounded-[18px] border border-[#E5EDF3] overflow-hidden" data-testid="fax-steps">
            {FAX_STEPS.map((s, j) => {
              const ok = j < idx
              const run = !failed && j === idx
              const label = j === 2 && failed ? t('shareHub.fax_failed', 'Failed') : t('shareHub.fax_' + s, s)
              return (
                <div key={s} className="flex gap-3 items-center px-3.5 py-3 border-t border-[#EEF3F7] first:border-t-0 text-[14.5px]" style={{ color: NAVY }}>
                  <em className="not-italic w-6 h-6 rounded-full grid place-items-center text-[12px] font-extrabold flex-none"
                      style={ok ? { background: '#E5F7EA', color: '#2A9B46' } : j === 2 && failed ? { background: '#FEF2F2', color: '#DC2626' } : run ? { background: BLUE, color: '#fff' } : { background: '#E9F8FF', color: BLUE }}>
                    {ok ? <Check size={13} /> : run ? <OrellaSpinner size={16} tone="inherit" /> : (j === 2 && failed ? '!' : j + 1)}
                  </em>
                  <span>{label}{j === 2 && to ? <small className="block text-[12px]" style={{ color: MUTE }}>{t('sendReport.toNumber', { defaultValue: 'To {{n}}', n: to })}</small> : null}</span>
                </div>
              )
            })}
          </div>
          <p className="text-[12px] text-center" style={{ color: MUTE }}>{t('sendReport.failNote', "If send fails, you'll get a notification and can try again.")}</p>
        </div>
        {/* PROD-1194 part C: pinned on top of the TabBar */}
        <BottomActionBar aboveTabBar className="[--bar-px:16px] flex flex-col gap-2">
          {failed && (
            <button onClick={() => { setPhase('confirm'); setConsent(false); setSend(null) }}
                    className="w-full h-[50px] rounded-full font-bold text-[15px] text-white" style={{ background: NAVY }}>
              {t('shareHub.tryAgain', 'Try again')}
            </button>
          )}
          <button onClick={() => navigate('/case/home')}
                  className="w-full h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2" style={{ background: BLUE }}>
            <Home size={18} /> {t('visitPrep.caseHome', 'Case Home')}
          </button>
        </BottomActionBar>
      </div>
    )
  }

  return (
    <div className="tabbar-screen-min-h bg-gray-50 flex flex-col">
      <CompactHeader stepLabel={t('shareHub.stepLabel', 'Share')} onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />
      <div className="flex-1 px-4 pt-3 pb-6 flex flex-col gap-3">
        <h2 className="text-[19px] font-extrabold tracking-tight" style={{ color: NAVY }}>{t('sendReport.title', 'Send your Case Report')}</h2>
        <div className="bg-white rounded-[18px] border border-[#E5EDF3] p-3.5 flex flex-col gap-2.5">
          <div>
            <b className="block text-[14.5px] font-bold" style={{ color: NAVY }}>{name}{provider?.credential ? `, ${provider.credential}` : ''}</b>
            {provider?.specialty && <span className="text-[12px]" style={{ color: MUTE }}>{provider.specialty}</span>}
          </div>
          <div className="flex flex-col gap-2 text-[13px]" style={{ color: BODY }}>
            {office && <div className="flex justify-between gap-3"><span>{t('sendReport.office', 'Office')}</span>
              <span className="flex flex-col items-end gap-1"><b className="font-semibold text-right" style={{ color: NAVY }}>{office}</b>
                {!confirmed && <span className="text-[11px] font-bold rounded-full px-2 py-0.5" style={{ color: '#B45309', background: '#FFF7E8' }} data-testid="send-address-unconfirmed">{t('findDoctor.addressUnconfirmed', 'Address not confirmed')}</span>}
              </span></div>}
            {provider?.phone && <div className="flex justify-between gap-3"><span>{t('sendReport.phone', 'Phone')}</span><b className="font-semibold tabular-nums" style={{ color: NAVY }}>{formatUsPhone(provider.phone) || provider.phone}</b></div>}
            {provider?.fax && !editing && <div className="flex justify-between gap-3"><span>{t('sendReport.fax', 'Fax')}</span><b className="font-semibold tabular-nums" style={{ color: NAVY }}>{formatUsPhone(provider.fax) || provider.fax}</b></div>}
            {provider?.fax && !editing && provider?.faxSource === 'found' && typeof hasText === 'function' && hasText('sendReport.faxFound') && <p className="text-[12px] leading-snug" style={{ color: NAVY }}>{t('sendReport.faxFound')}</p>}
          </div>
          {provider?.fax && !editing ? (
            <button type="button" className="self-start text-[13px] font-bold" style={{ color: BLUE }} onClick={() => setEditing(true)}>
              {t('sendReport.wrongNumber', 'Wrong number? Enter it')}
            </button>
          ) : (
            <>
              {!provider?.fax && (
                <p className="text-[12px]" style={{ color: '#B45309' }}>
                  {provider?.phone
                    ? t('sendReport.noFaxCall', { defaultValue: 'No fax number is listed for this office. Call {{phone}} and ask for it.', phone: formatUsPhone(provider.phone) || provider.phone })
                    : t('sendReport.noFax', 'No fax number is listed for this office. Call them and ask for it.')}
                </p>
              )}
              <label htmlFor="faxin" className="text-[11px] font-bold uppercase tracking-[.09em]" style={{ color: NAVY }}>{t('sendReport.faxNumber', 'Fax number')}</label>
              <input id="faxin" inputMode="tel" autoComplete="off" value={fax} onChange={(e) => setFax(e.target.value)}
                     placeholder="(415) 555-0000" className="rounded-xl px-3 py-2.5 text-[14.5px] tabular-nums outline-none"
                     style={{ border: '1.5px solid #E5EDF3', color: NAVY }} />
              {!provider?.fax && <small className="text-[11.5px]" style={{ color: BODY }}>{t('sendReport.faxHint', "It's usually on your appointment card or the office's website.")}</small>}
              {/* s101 Build 67 (Dr. Rami): "Clicking edit does not have a cancel" - back to the number on file. */}
              {provider?.fax && (
                <button type="button" data-cta="send-report-fax-cancel" className="self-start text-[13px] font-bold" style={{ color: BLUE }}
                        onClick={() => { setEditing(false); setFax('') }}>
                  {t('sendReport.useOnFile', 'Cancel · use the number on file')}
                </button>
              )}
            </>
          )}
        </div>
        {!confirmed && (
          <div className="rounded-[14px] p-3 text-[12.5px] leading-snug" style={{ background: '#F5FBFF', border: '1px solid #A7D9FC', color: NAVY }} data-testid="send-address-note">
            {t('sendReport.addressUnconfirmedNote', "We don't have this office's street address yet. When the office opens your case, we'll ask them to confirm it.")}
          </div>
        )}
        <div className="bg-white rounded-[18px] border border-[#E5EDF3] p-3.5 flex flex-col gap-2 text-[13px]" style={{ color: BODY }}>
          <p className="text-[11px] font-bold uppercase tracking-[.09em]" style={{ color: MUTE }}>{t('sendReport.whatWeSend', "What we'll send")}</p>
          <div className="flex justify-between gap-3"><span>{t('sendReport.coverSheet', 'Cover sheet')}</span><b className="font-semibold text-right" style={{ color: NAVY }}>{t('sendReport.coverSheetValue', 'With your share code')}</b></div>
          <div className="flex justify-between gap-3"><span>{t('shareHub.reportTitle', 'Your Case Report')}</span><b className="font-semibold" style={{ color: NAVY }}>PDF</b></div>
        </div>
        <label className="flex gap-2.5 items-start text-[13px] leading-snug cursor-pointer" style={{ color: BODY }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-[18px] h-[18px] mt-0.5 flex-none" style={{ accentColor: BLUE }} />
          <span>{t('sendReport.consent', 'I consent for Orella to send my Case Report, which includes my health information, to this office.')}</span>
        </label>
      </div>
      {/* PROD-1194 part C: "Send my Case Report" pinned on top of the TabBar, so it stays in sight under
          the office card, the address note and the consent box on a small iPhone */}
      <BottomActionBar aboveTabBar className="[--bar-px:16px]"
        note={error && <p className="text-[13px] text-center mb-2" style={{ color: '#B45309' }} role="alert">{error}</p>}>
        <button type="button" onClick={doSend} disabled={!canSend} data-cta="send-report-fax"
                className="w-full h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: canSend ? NAVY : '#CFDCE7' }}>
          {busy && <OrellaSpinner size={18} tone="inherit" />}
          {t('sendReport.send', 'Send my Case Report')}
        </button>
      </BottomActionBar>
    </div>
  )
}
